/**
 * FreeCell — the open-information variant. PURE rules; no React, no DOM.
 *
 * Layout
 *   - 8 tableau columns, ALL cards face-up from the start (no hidden cards).
 *     Columns 0–3 get 7 cards, columns 4–7 get 6 (52 = 4×7 + 4×6).
 *   - 4 free cells (each holds at most one card).
 *   - 4 foundations (built up by suit from the Ace).
 *
 * Rules
 *   - Tableau builds DOWN in ALTERNATING color.
 *   - Any card may move to an EMPTY tableau column (no King restriction).
 *   - A free cell holds one card; a foundation builds up by suit from the Ace.
 *   - The classic engine moves one card at a time, but players expect to drag a
 *     whole ordered run. A "supermove" is legal only if it could be performed
 *     one-card-at-a-time using the available free cells + empty columns:
 *
 *         maxRun = (1 + freeCells) × 2^(emptyColumns)
 *
 *     When the DESTINATION is an empty column, that column can't be used as an
 *     intermediate, so we use 2^(emptyColumns − 1) for the count.
 *   - Win = all 52 on the foundations.
 *
 * No stock/waste/flip — FreeCell is pure tableau + cells + foundations.
 */
import { type Card, makeDeck, seededShuffle } from './cards';
import {
  alternates,
  findZone,
  locateCard,
  topCard,
  withCards,
  withZones,
  zonesOfKind,
} from './helpers';
import type { GameState, Move, SolitaireVariant, Zone } from './types';

const TABLEAU_COUNT = 8;
const FREECELL_COUNT = 4;
const FOUNDATION_COUNT = 4;

// ---------------------------------------------------------------------------
// Deal
// ---------------------------------------------------------------------------

export function deal(seed: number): GameState {
  const deck = seededShuffle(makeDeck(), seed).map((c) => ({ ...c, faceUp: true }));
  const zones: Zone[] = [];

  // 8 columns, dealt round-robin: the first 4 columns get 7, the last 4 get 6.
  const columns: Card[][] = Array.from({ length: TABLEAU_COUNT }, () => []);
  deck.forEach((card, i) => {
    columns[i % TABLEAU_COUNT]!.push(card);
  });
  columns.forEach((cards, p) => {
    zones.push({ id: `tableau-${p}`, kind: 'tableau', cards, col: p });
  });

  for (let i = 0; i < FREECELL_COUNT; i += 1) {
    zones.push({ id: `free-${i}`, kind: 'freecell', cards: [], col: i });
  }
  for (let f = 0; f < FOUNDATION_COUNT; f += 1) {
    zones.push({ id: `foundation-${f}`, kind: 'foundation', cards: [], col: f });
  }

  return { variant: 'freecell', zones, moveCount: 0, won: false };
}

// ---------------------------------------------------------------------------
// Rule predicates
// ---------------------------------------------------------------------------

function canPlaceOnTableau(card: Card, dest: Zone): boolean {
  const top = topCard(dest);
  if (!top) return true; // any card to an empty column
  return alternates(card, top) && top.rank === card.rank + 1;
}

function canPlaceOnFoundation(card: Card, dest: Zone): boolean {
  const top = topCard(dest);
  if (!top) return card.rank === 1;
  return top.suit === card.suit && card.rank === top.rank + 1;
}

/** Is the slice from `index` to the top a descending-alternating-color run? */
function isMovableRun(src: Zone, index: number): boolean {
  const cards = src.cards;
  if (index < 0 || index >= cards.length) return false;
  for (let k = index; k < cards.length - 1; k += 1) {
    const a = cards[k]!;
    const b = cards[k + 1]!;
    if (!alternates(a, b) || a.rank !== b.rank + 1) return false;
  }
  return true;
}

/** Count empty free cells. */
function freeCellCount(state: GameState): number {
  return zonesOfKind(state, 'freecell').filter((z) => z.cards.length === 0).length;
}

/** Count empty tableau columns. */
function emptyColumnCount(state: GameState): number {
  return zonesOfKind(state, 'tableau').filter((z) => z.cards.length === 0).length;
}

/**
 * The biggest run that can be moved given free cells + empty columns. When the
 * destination is itself an empty column, that column is not available as an
 * intermediate, so it doesn't count toward the doubling.
 */
export function maxSupermove(state: GameState, destIsEmptyColumn: boolean): number {
  const free = freeCellCount(state);
  const empties = emptyColumnCount(state) - (destIsEmptyColumn ? 1 : 0);
  const usableEmpties = Math.max(0, empties);
  return (1 + free) * 2 ** usableEmpties;
}

// ---------------------------------------------------------------------------
// Legal moves
// ---------------------------------------------------------------------------

export function legalMoves(state: GameState): Move[] {
  const moves: Move[] = [];
  const tableaus = zonesOfKind(state, 'tableau');
  const foundations = zonesOfKind(state, 'foundation');
  const frees = zonesOfKind(state, 'freecell');

  // Sources that expose a single top card: free cells + (we also treat the
  // tableau top as a single-card source for foundation/freecell targets).
  const singleSources: { zone: Zone; card: Card }[] = [];
  for (const fc of frees) {
    const top = topCard(fc);
    if (top) singleSources.push({ zone: fc, card: top });
  }
  for (const t of tableaus) {
    const top = topCard(t);
    if (top) singleSources.push({ zone: t, card: top });
  }

  // Single-card moves → foundation / freecell.
  for (const { zone: src, card } of singleSources) {
    for (const f of foundations) {
      if (canPlaceOnFoundation(card, f)) {
        moves.push({ kind: 'move', from: src.id, to: f.id, cardId: card.id });
      }
    }
    // Into an empty free cell (only from tableau or another cell — moving a
    // card from a cell to a cell is a pointless shuffle, so skip cell→cell).
    if (src.kind !== 'freecell') {
      const emptyCell = frees.find((fc) => fc.cards.length === 0);
      if (emptyCell) {
        moves.push({ kind: 'move', from: src.id, to: emptyCell.id, cardId: card.id });
      }
    }
  }

  // Run moves between tableaus (and free-cell card → tableau, run length 1).
  for (const src of [...tableaus, ...frees]) {
    for (let i = 0; i < src.cards.length; i += 1) {
      // Free cells only ever move their single card (i can only be 0).
      if (src.kind === 'freecell' && i !== 0) break;
      if (!isMovableRun(src, i)) continue;
      const head = src.cards[i]!;
      const runLen = src.cards.length - i;
      for (const dest of tableaus) {
        if (dest.id === src.id) continue;
        if (!canPlaceOnTableau(head, dest)) continue;
        const destEmpty = dest.cards.length === 0;
        if (runLen > maxSupermove(state, destEmpty)) continue;
        moves.push({ kind: 'move', from: src.id, to: dest.id, cardId: head.id });
      }
    }
  }

  return moves;
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

export function applyMove(state: GameState, move: Move): GameState {
  if (move.kind !== 'move' && move.kind !== 'auto') {
    throw new Error(`freecell: unsupported move kind "${move.kind}"`);
  }
  const located = locateCard(state, move.cardId);
  if (!located) throw new Error(`freecell: card not found ${move.cardId}`);
  const src = located.zone;
  const index = located.index;
  const dest = findZone(state, move.to);
  if (!dest) throw new Error(`freecell: dest not found ${move.to}`);
  if (src.id === dest.id) throw new Error('freecell: move to same zone');

  const moving = src.cards.slice(index);
  const head = moving[0]!;

  if (dest.kind === 'foundation') {
    if (moving.length !== 1) throw new Error('freecell: only one card to a foundation');
    if (!canPlaceOnFoundation(head, dest)) throw new Error('freecell: illegal foundation move');
  } else if (dest.kind === 'freecell') {
    if (moving.length !== 1) throw new Error('freecell: only one card to a free cell');
    if (dest.cards.length !== 0) throw new Error('freecell: free cell occupied');
  } else if (dest.kind === 'tableau') {
    if (!isMovableRun(src, index)) throw new Error('freecell: not a movable run');
    if (!canPlaceOnTableau(head, dest)) throw new Error('freecell: illegal tableau move');
    const destEmpty = dest.cards.length === 0;
    if (moving.length > maxSupermove(state, destEmpty)) {
      throw new Error('freecell: run exceeds supermove limit');
    }
  } else {
    throw new Error(`freecell: cannot move onto a ${dest.kind}`);
  }

  const next = withZones(state, {
    [src.id]: withCards(src, src.cards.slice(0, index)),
    [dest.id]: withCards(dest, [...dest.cards, ...moving]),
  });
  next.won = isWon(next);
  return next;
}

// ---------------------------------------------------------------------------
// Win + auto-move
// ---------------------------------------------------------------------------

export function isWon(state: GameState): boolean {
  const total = zonesOfKind(state, 'foundation').reduce((n, f) => n + f.cards.length, 0);
  return total === 52;
}

/**
 * Next safe auto-send to a foundation, or null. Same "safe" heuristic as
 * Klondike: low cards always; otherwise only when both opposite-color
 * foundations are at least one rank below (no tableau card still needs it).
 */
export function autoMove(state: GameState): Move | null {
  const foundations = zonesOfKind(state, 'foundation');
  const rankBySuit: Partial<Record<Card['suit'], number>> = {};
  for (const f of foundations) {
    const top = topCard(f);
    if (top) rankBySuit[top.suit] = top.rank;
  }
  const minOppositeRank = (card: Card): number => {
    const oppSuits: Card['suit'][] =
      card.color === 'red' ? ['spades', 'clubs'] : ['hearts', 'diamonds'];
    let min = 13;
    for (const s of oppSuits) min = Math.min(min, rankBySuit[s] ?? 0);
    return min;
  };

  const candidates: { zone: Zone; card: Card }[] = [];
  for (const z of [...zonesOfKind(state, 'freecell'), ...zonesOfKind(state, 'tableau')]) {
    const top = topCard(z);
    if (top) candidates.push({ zone: z, card: top });
  }
  for (const { card } of candidates) {
    const dest = foundations.find((f) => canPlaceOnFoundation(card, f));
    if (!dest) continue;
    if (card.rank <= 2 || minOppositeRank(card) >= card.rank - 1) {
      return { kind: 'auto', to: dest.id, cardId: card.id };
    }
  }
  return null;
}

export const freecell: SolitaireVariant = {
  id: 'freecell',
  name: 'FreeCell',
  deal,
  legalMoves,
  applyMove,
  isWon,
  autoMove,
};
